<div class="media-gallery">
  <!-- Gallery Header with Controls -->
  <div class="gallery-header" *ngIf="showControls">
    <div class="header-left">
      <h3 class="gallery-title">
        Media Gallery 
        <p-tag 
          *ngIf="filteredMedia.length > 0" 
          [value]="filteredMedia.length + ' items'" 
          severity="info">
        </p-tag>
      </h3>
    </div>

    <div class="header-right">
      <!-- Search -->
      <div class="search-container">
        <span class="p-input-icon-left">
          <i class="pi pi-search"></i>
          <input 
            type="text" 
            pInputText 
            placeholder="Search media..." 
            [(ngModel)]="filterValue"
            (input)="sortMedia()"
            class="search-input">
        </span>
      </div>

      <!-- Sort Options -->
      <div class="sort-controls">
        <p-select 
          [options]="getSortOptions()" 
          [(ngModel)]="sortBy"
          (selectionChange)="sortMedia()"
          optionLabel="label" 
          optionValue="value"
          placeholder="Sort by..."
          class="sort-dropdown">
        </p-select>

        <p-button 
          [icon]="getSortOrderIcon()"
          [label]="getSortOrderLabel()"
          (onClick)="toggleSortOrder()"
          severity="secondary"
          [outlined]="true"
          [pTooltip]="getSortTooltip()"
          class="sort-order-btn">
        </p-button>
      </div>

      <!-- Media Type Selector -->
      <div class="media-type-selector">
        <p-selectbutton 
          [options]="mediaTypeOptions" 
          [(ngModel)]="selectedMediaType" 
          optionLabel="label"
          optionValue="value">
          <ng-template #item let-item>
            <i [class]="item.icon"></i>
          </ng-template>
        </p-selectbutton>
      </div>

      <!-- Grid Size Controls -->
      <div class="grid-controls">
        <p-button 
          *ngFor="let size of gridSizeOptions" 
          [icon]="size.icon"
          [severity]="gridSettings.size === size.value ? 'primary' : 'secondary'"
          [outlined]="gridSettings.size !== size.value"
          (onClick)="updateGridSize(size.value)"
          [pTooltip]="size.label"
          class="grid-size-btn">
        </p-button>
      </div>

    </div>
  </div>

  <!-- Selection Controls -->
  <div class="selection-controls" *ngIf="allowBatchOperations && filteredMedia.length > 0">
    <div class="selection-left">
      <p-checkbox 
        [(ngModel)]="selectAll"
        (onChange)="toggleSelectAll()"
        label="Select All"
        binary="true">
      </p-checkbox>
      
      <span class="selection-count" *ngIf="selectedItems.size > 0">
        {{ selectedItems.size }} of {{ filteredMedia.length }} selected
      </span>
    </div>

    <div class="selection-right" *ngIf="selectedItems.size > 0">
      <p-splitButton 
        label="Batch Actions" 
        icon="pi pi-cog"
        [model]="batchMenuItems"
        severity="secondary">
      </p-splitButton>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="loading">
    <p-progressSpinner></p-progressSpinner>
    <p>Loading media files...</p>
  </div>
  
  <!-- Composition Processing Status -->
  <div class="composition-status" *ngIf="hasActiveCompositions && compositionStatus">
    <p-card styleClass="composition-status-card">
      <div class="composition-content">
        <div class="composition-icon">
          <p-progressSpinner styleClass="w-2rem h-2rem" strokeWidth="4" animationDuration="1s"></p-progressSpinner>
        </div>
        <div class="composition-text">
          <h4 class="m-0 mb-1">Processing Video Compositions</h4>
          <p class="m-0 text-sm text-color-secondary">
            <span *ngIf="compositionStatus.summary.processing > 0">
              {{ compositionStatus.summary.processing }} video(s) being processed...
            </span>
            <span *ngIf="compositionStatus.summary.pending > 0 && compositionStatus.summary.processing === 0">
              {{ compositionStatus.summary.pending }} video(s) queued for processing...
            </span>
          </p>
        </div>
      </div>
    </p-card>
  </div>

  <!-- Empty State -->
  <div class="empty-state" *ngIf="!loading && filteredMedia.length === 0">
    <div class="empty-content">
      <i class="pi pi-images empty-icon"></i>
      <h4>No Media Files</h4>
      <p *ngIf="filterValue">No media files match your search.</p>
      <p *ngIf="!filterValue && media.length === 0">No media files have been captured yet.</p>
      <p *ngIf="!filterValue && media.length > 0">All media files are filtered out.</p>
    </div>
  </div>

  <!-- Media Section -->
  <div class="media-section" *ngIf="!loading && visibleMedia.length > 0">
    <div class="section-header">

    </div>
    
    <div 
      class="media-grid" 
      [ngClass]="'grid-' + gridSettings.size"
      cdkDropList
      (cdkDropListDropped)="onDrop($event)"
      [cdkDropListDisabled]="!allowReorder">
      
      <div 
        *ngFor="let item of visibleMedia; let i = index; trackBy: trackByMediaId"
        class="media-item"
        [ngClass]="{
          'selected': isItemSelected(item),
          'draggable': allowReorder,
          'video-item': isVideo(item),
          'photo-item': isImage(item)
        }"
        cdkDrag
        [cdkDragDisabled]="!allowReorder">
        
        <!-- Selection Checkbox -->
        <div class="item-selection" *ngIf="allowBatchOperations">
          <p-checkbox 
            [ngModel]="isItemSelected(item)"
            (onChange)="toggleItemSelection(item)"
            binary="true">
          </p-checkbox>
        </div>

        <!-- Media Preview -->
        <div class="item-preview" (click)="isVideo(item) ? openVideoGalleria(i) : openPhotoGalleria(i)">
          <div class="image-container">
            <!-- Video Preview -->
            <div class="video-container" *ngIf="isVideo(item)">
              <video 
                [src]="getPhotoUrl(item, false)"
                class="media-video"
                preload="metadata"
                muted
                [poster]="getPhotoUrl(item, true)"
                (error)="onImageError($event)">
              </video>
              <div class="video-overlay">
                <i class="pi pi-play-circle video-play-icon"></i>
              </div>
            </div>
            
            <!-- Photo Preview -->
            <img 
              *ngIf="isImage(item)"
              [src]="getPhotoUrl(item, true)" 
              [alt]="item.name || 'Photo'"
              [title]="item.name || 'Photo'"
              class="media-image"
              loading="lazy"
              (error)="onImageError($event)">
            
            <!-- Status Overlay -->
            <div class="status-overlay" *ngIf="item.status && item.status !== 'ready'">
              <p-progressSpinner 
                *ngIf="item.status === 'uploading'"
                strokeWidth="4"
                animationDuration="1s">
              </p-progressSpinner>
              <i 
                *ngIf="item.status === 'error'" 
                class="pi pi-exclamation-triangle error-icon">
              </i>
            </div>

            <!-- Progress Bar -->
            <div class="upload-progress" *ngIf="item.progress !== undefined">
              <div class="progress-bar" [style.width.%]="item.progress"></div>
            </div>
          </div>

          <!-- Media Label -->
          <div class="item-label-overlay">
            <span class="label-text" 
                  [class.no-label]="!item.label"
                  (click)="$event.stopPropagation(); openLabelDialog(item)">
              {{ item.label || 'Click to Label' }}
            </span>
            <span class="item-metadata">
              <span class="metadata-item">{{ item.createdAt | date:'short' }}</span>
              <span class="metadata-separator">•</span>
              <span class="metadata-item">{{ formatFileSize(item.size) }}</span>
            </span>
          </div>
        </div>

        <!-- Item Actions -->
        <div class="item-actions">
          <p-button 
            icon="pi pi-tag"
            (onClick)="openLabelDialog(item)"
            severity="secondary"
            [outlined]="true"
            pTooltip="Add Label"
            size="small">
          </p-button>
          
          <p-button 
            icon="pi pi-eye"
            (onClick)="isVideo(item) ? openVideoGalleria(i) : openPhotoGalleria(i)"
            severity="secondary"
            [outlined]="true"
            [pTooltip]="isVideo(item) ? 'Play Video' : 'View Full Size'"
            size="small">
          </p-button>

          <p-button 
            icon="pi pi-download"
            (onClick)="downloadSingleItem(item)"
            severity="secondary"
            [outlined]="true"
            pTooltip="Download"
            size="small">
          </p-button>
        </div>

        <!-- Drag Handle -->
        <div class="drag-handle" *ngIf="allowReorder" cdkDragHandle>
          <i class="pi pi-bars"></i>
        </div>
      </div>
    </div>
  </div>

  <!-- Media Preview Dialog -->
  <p-dialog 
    [(visible)]="displayGalleria" 
    [header]="(getCurrentPhoto()?.label || 'Media Preview') + ' (' + (selectedMediaIndex + 1) + ' of ' + filteredMedia.length + ')'"
    [modal]="true"
    [dismissableMask]="true"
    [closeOnEscape]="true"
    [maximizable]="false"
    [draggable]="false"
    [resizable]="false"
    styleClass="media-preview-dialog"
    [style]="{width: '120vh', height: '90vh'}">
    
    <div class="media-preview-container">
      <!-- Navigation Buttons -->
      <p-button 
        icon="pi pi-chevron-left"
        [rounded]="true"
        [text]="true"
        severity="secondary"
        (onClick)="previousPhoto()"
        [disabled]="selectedMediaIndex === 0 && !isCircularNavigation"
        pTooltip="Previous"
        class="nav-button-left"
        aria-label="Previous photo">
      </p-button>
      
      <!-- Media Display Area -->
      <div class="media-display-area">
        <div class="media-content">
          <!-- Image Display -->
          <div class="media-wrapper" *ngIf="getCurrentPhoto() && isImage(getCurrentPhoto())">
            <img 
              [src]="getPhotoUrl(getCurrentPhoto(), false)" 
              [alt]="getCurrentPhoto()?.name || 'Photo preview'"
              [title]="getCurrentPhoto()?.name || 'Photo preview'"
              class="preview-image"
              (error)="onImageError($event)" />
          </div>
          
          <!-- Video Display -->
          <div class="media-wrapper" *ngIf="getCurrentPhoto() && isVideo(getCurrentPhoto())">
            <video 
              [src]="getPhotoUrl(getCurrentPhoto(), false)"
              controls
              preload="metadata"
              class="preview-video"
              (error)="onImageError($event)">
              Your browser does not support the video tag.
            </video>
          </div>
          
          <!-- Media Info -->
          <div class="preview-info-content">
            <span class="media-label" [class.no-label]="!getCurrentPhoto()?.label">
              {{ getCurrentPhoto()?.label || 'Not Labeled' }}
            </span>
            <span class="media-info">
              <span class="info-item">{{ getCurrentPhoto()?.createdAt | date:'short' }}</span>
              <span class="info-separator">•</span>
              <span class="info-item">{{ formatFileSize(getCurrentPhoto()?.size || 0) }}</span>
            </span>
          </div>
        </div>
      </div>
      
      <p-button 
        icon="pi pi-chevron-right"
        [rounded]="true"
        [text]="true"
        severity="secondary"
        (onClick)="nextPhoto()"
        [disabled]="selectedMediaIndex === filteredMedia.length - 1 && !isCircularNavigation"
        pTooltip="Next"
        class="nav-button-right"
        aria-label="Next photo">
      </p-button>
    </div>
  </p-dialog>

  <!-- Label Dialog -->
  <p-dialog 
    [(visible)]="showLabelDialog" 
    header="Add/Edit Label" 
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    styleClass="label-dialog">
    
    <form [formGroup]="labelForm" (ngSubmit)="saveLabel()">
      <div class="dialog-content">
        <div class="form-field">
          <label for="label">Label</label>
          <input 
            id="label"
            type="text" 
            pInputText 
            formControlName="label"
            placeholder="Enter a label for the selected media"
            class="full-width">
        </div>
        
        <div class="selected-count" *ngIf="labelingItems.length > 1">
          Applying to {{ labelingItems.length }} selected items
        </div>
      </div>

      <div class="dialog-actions">
        <p-button 
          label="Cancel" 
          icon="pi pi-times" 
          (onClick)="showLabelDialog = false"
          severity="secondary"
          [outlined]="true">
        </p-button>
        
        <p-button 
          label="Save" 
          icon="pi pi-check" 
          type="submit"
          [disabled]="labelForm.invalid">
        </p-button>
      </div>
    </form>
  </p-dialog>
</div>